import {Injectable} from '@angular/core';
import {AttributionLineTeacherDetail} from '@model/attribution-line-teacher-detail';
import {combineLatest, forkJoin, Observable} from 'rxjs';
import {select, Store} from '@ngrx/store';
import {
  getAbsenceVerificationConfirmation,
  getAttendanceRegistries,
  getClassesAndVerticalClassesAccessibleToConnectedStaffMember,
  getClassesRelatedToConnectedStaffMember,
  getClassesWithClassLinks,
  getSchedule,
  getStudentAbsenceOriginByStudentIdByHours,
  getSummerPermanences,
  selectedProfile,
  StoreState
} from '@redux/reducers';
import {AttributionService} from '@services/attribution.service';
import {filter, map, switchMap, take} from 'rxjs/operators';
import {
  CurrentYearAttributionsLoaded,
  NextYearAttributionsLoaded,
  SchoolDiaryByAttributionLoaded
} from '@redux/actions/staffmembers-consult.action';
import {School} from '@model/school';
import {Profile} from '@model/profile';
import {StudentService} from '@services/student.service';
import {
  AttendanceRegistriesLoadedAction,
  AttendanceRegistriesResetAction,
  ScheduleLoadedAction,
  SummerPermanenceAssignedAction,
  SummerPermanenceDeletedAction,
  SummerPermanenceLoadedAction,
  SummerPermanenceSavedAction,
  SummerPermanenceUnassignedAction
} from '@redux/actions/school.action';
import {SchoolService} from '@services/school.service';
import {Schedule} from '@model/schedule';
import {studentAbsencesByDateLoaded, studentAbsenceVerificationsConfirmationLoaded, studentAbsenceVerificationsConfirmationReset} from '@redux/actions/student-absence.action';
import {SharedFacadeService} from '@shared/shared-facade.service';
import {StaffMember} from '@model/staffMember';
import {TeacherPlanningService} from '@services/teacher-planning.service';
import {SchoolDiaryEntryEvent} from '@model/teacher-planning-event';
import {AgendaActivity} from '@model/agenda-activity';
import {StudentClassExclusionsLoaded, StudentEarlyDeparturesLoaded, StudentLateArrivalsLoaded, StudentRemarksLoaded} from '@redux/actions/students.action';
import {StudentRemark} from '@model/student-remark';
import {StudentLateArrival} from '@model/student-late-arrival';
import {Class, ClassWithHorizonClassLink} from '@model/class';
import {DateTime} from 'luxon';
import {teacherPlanningEventsLoaded} from '@redux/actions/teacher-planning.action';
import {SummerPermanence} from '@model/summer-permanence';
import {AttendanceRegistryByClass} from '@model/attendance-registry';
import {documentGenerationRequestStarted, documentGenerationRequestStopped} from '@redux/actions/ui.action';
import {SchoolSetting} from "@model/school-setting";
import {SchoolSettingKey} from "@model/school-setting-key";
import {SharedDocument} from '@model/shared-documents';
import {DocumentService} from '@services/document.service';
import {SharedDocumentDeleted, SharedDocumentUploaded} from '@redux/actions/shared-documents.action';
import {SharedDocumentService} from '@services/shared-document.service';
import {StudentEarlyDeparture} from '@model/student-early-departure';
import {AttributionSummary} from "@model/attribution-summary";
import {AbsenceVerificationConfirmation} from '@model/absences-verification';
import {StudentClassExclusion} from '@model/student-class-exclusion';
import {ClassAndVerticalClass} from '@model/class-and-vertical-class';

@Injectable()
export class StaffmemberConsultFacadeService {

  school$: Observable<School>;
  currentYearAttributionLines$: Observable<AttributionLineTeacherDetail[]>;
  nextYearAttributionLines$: Observable<AttributionLineTeacherDetail[]>;
  schoolDiaryByAttribution$: Observable<SchoolDiaryEntryEvent[]>;
  profile$: Observable<Profile>;
  schedule$: Observable<Schedule>;
  studentAbsencesOriginByStudentIdByHours$: Observable<any[]>;
  staffMembers$: Observable<StaffMember[]>;
  attributionsActivesForTeacher$: Observable<AttributionSummary[]>;
  attributionLinesByTeachers$: Observable<{ [teacherId: number]: AttributionLineTeacherDetail[] }>;
  agendaActivities$: Observable<AgendaActivity[]>;
  studentRemarksByDay$: Observable<StudentRemark[]>;
  studentLateArrivalsByDay$: Observable<StudentLateArrival[]>;
  studentEarlyDeparturesByDay$: Observable<StudentEarlyDeparture[]>;
  studentExclusionsByDay$: Observable<StudentClassExclusion[]>;
  isMobile$: Observable<boolean>;
  classes$: Observable<Class[]>;
  classesRelatedToConnectedStaffMember$: Observable<Class[]>;
  planningEventsByHoursByDates$: Observable<any>;
  summerPermanences$: Observable<SummerPermanence[]>;
  isAdminStaffMember$: Observable<boolean>;
  isEducatorStaffMember$: Observable<boolean>;
  isTeacherStaffMember$: Observable<boolean>;
  attendanceRegistries$: Observable<AttendanceRegistryByClass[]>;
  documentGenerationRequestOngoing$: Observable<boolean>;
  schoolSettings$: Observable<SchoolSetting[]>;
  isAbsenceForDayOffEnabled$: Observable<boolean>;
  sharedDocuments$: Observable<SharedDocument[]>;
  absenceVerificationConfirmation$: Observable<{[studentId: number]: AbsenceVerificationConfirmation[]}>;
  classesWithHorizonClassLinks$: Observable<ClassWithHorizonClassLink[]>;
  classesAndVerticalClassesAccessibleToConnectedStaffMember$: Observable<ClassAndVerticalClass[]>;

  constructor(private _store: Store<StoreState>,
              private _schoolService: SchoolService,
              private _attributionService: AttributionService,
              private _studentService: StudentService,
              private _sharedFacade: SharedFacadeService,
              private _documentService: DocumentService,
              private _sharedDocumentService: SharedDocumentService,
              private _teacherPlanningService: TeacherPlanningService) {
    this.school$ = this._sharedFacade.school$;
    this.currentYearAttributionLines$ = this._store.pipe(
      select(state => state.staffMemberConsult.currentYearAttributionLines)
    );
    this.nextYearAttributionLines$ = this._store.pipe(
      select(state => state.staffMemberConsult.nextYearAttributionLines)
    );
    this.profile$ = this._store.pipe(selectedProfile);
    this.schedule$ = this._store.pipe(
      select(getSchedule)
    );
    this.studentAbsencesOriginByStudentIdByHours$ = this._store.pipe(
      select(getStudentAbsenceOriginByStudentIdByHours)
    );
    this.staffMembers$ = this._sharedFacade.staffMembers$;
    this.attributionsActivesForTeacher$ = this._sharedFacade.attributionsActivesForTeacher$;
    this.attributionLinesByTeachers$ = this._sharedFacade.attributionLinesByTeachers$;
    this.agendaActivities$ = this._sharedFacade.agendaActivities$;
    this.schoolDiaryByAttribution$ = this._store.pipe(
      select(state => state.staffMemberConsult.schoolDiaryByAttribution)
    );
    this.studentRemarksByDay$ = this._sharedFacade.studentRemarks$.pipe(map(page => page.content));
    this.studentLateArrivalsByDay$ = this._sharedFacade.studentLateArrivals$;
    this.studentEarlyDeparturesByDay$ = this._sharedFacade.studentEarlyDepartures$;
    this.isMobile$ = this._sharedFacade.isMobile$;
    this.classes$ = this._sharedFacade.classes$;
    this.classesRelatedToConnectedStaffMember$ = this._store.select(getClassesRelatedToConnectedStaffMember);
    this.planningEventsByHoursByDates$ = this._sharedFacade.planningEventsByHoursByDates$;
    this.summerPermanences$ = this._store.pipe(select(getSummerPermanences));
    this.isAdminStaffMember$ = this._sharedFacade.isAdminStaffMember$;
    this.isEducatorStaffMember$ = this._sharedFacade.isEducatorStaffMember$;
    this.isTeacherStaffMember$ = this._sharedFacade.isTeacherStaffMember$;
    this.attendanceRegistries$ = this._store.pipe(select(getAttendanceRegistries));
    this.documentGenerationRequestOngoing$ = this._sharedFacade.documentGenerationRequestOngoing$;
    this.schoolSettings$ = this._sharedFacade.schoolSettings$;
    this.isAbsenceForDayOffEnabled$ = this._sharedFacade.schoolSettings$.pipe(
      map(settings => !!settings.find(s => s.key === SchoolSettingKey.ABSENCE_FOR_DAY_OFF)?.enabled)
    );
    this.sharedDocuments$ = this._sharedFacade.sharedDocuments$;
    this.absenceVerificationConfirmation$ = this._store.pipe(select(getAbsenceVerificationConfirmation));
    this.studentExclusionsByDay$ = this._sharedFacade.studentClassExclusions$;
    this.classesWithHorizonClassLinks$ = this._store.pipe(select(getClassesWithClassLinks)) as Observable<ClassWithHorizonClassLink[]>;
    this.classesAndVerticalClassesAccessibleToConnectedStaffMember$ = this._store.pipe(select(getClassesAndVerticalClassesAccessibleToConnectedStaffMember)) as Observable<ClassAndVerticalClass[]>;
  }

  loadAttributionLinesForTeacher() {
    combineLatest([this.school$, this._store.pipe(selectedProfile)]).pipe(
      take(1),
      switchMap(([school, profile]) =>
        this._attributionService.loadAttributionLinesForTeacher(school.id, school.activeSchoolYear, profile.id))
    ).subscribe(attributionLines => this._store.dispatch(new CurrentYearAttributionsLoaded(attributionLines)));
    combineLatest([this.school$, this.schoolSettings$, this._store.pipe(selectedProfile)]).pipe(
      take(1),
      filter(([school, settings, profile]) =>
        settings.find(s => s.key == SchoolSettingKey.CONSULT_ATTRIBUTION_BY_TEACHER).enabled),
      switchMap(([school, settings, profile]) =>
        this._attributionService.loadAttributionLinesForTeacher(school.id, Number(school.activeSchoolYear) + 1, profile.id))
    ).subscribe(attributionLines => this._store.dispatch(new NextYearAttributionsLoaded(attributionLines)));
  }

  loadStudentAbsenceByDate(date: string) {
    this._store.dispatch(studentAbsenceVerificationsConfirmationReset());
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.loadStudentAbsenceByDate(school.id, date))
    ).subscribe(absences => this._store.dispatch(studentAbsencesByDateLoaded({studentAbsences: absences})));
  }

  loadStudentAbsenceVerificationConfirmation(studentId: number, date: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.getStudentAbsenceVerificationConfirmation(school.id, studentId, date))
    ).subscribe(absenceVerificationsConfirmation => this._store.dispatch(
      studentAbsenceVerificationsConfirmationLoaded({studentId, absenceVerificationsConfirmation})
    ));
  }

  loadSchedule() {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.getSchedule(school.id, school.activeSchoolYear))
    ).subscribe(schedule => this._store.dispatch(new ScheduleLoadedAction(schedule)));
  }

  loadStaffMembers() {
    this._sharedFacade.loadStaffMembers();
  }

  loadAttributionsActivesForTeacher(teacherId: number, year: number) {
    this._sharedFacade.loadAttributionsActivesForTeacher(teacherId, year);
  }

  loadSchoolDiaryByAttribution(attributionId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._teacherPlanningService.loadSchoolDiaryByAttribution(school.id, attributionId))
    ).subscribe(schoolDiaryEntryEvents => this._store.dispatch(new SchoolDiaryByAttributionLoaded(schoolDiaryEntryEvents)));
  }

  resetSchoolDiaryByAttribution() {
    this._store.dispatch(new SchoolDiaryByAttributionLoaded([]));
  }

  loadAgendaActivities(fromDate: DateTime) {
    this._sharedFacade.loadAgendaActivities(fromDate);
  }

  loadStudentRemarksByDay(date: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.getStudentsRemarksByDay(school.id, date))
    ).subscribe(remarks => this._store.dispatch(new StudentRemarksLoaded(remarks)));
  }

  resetStudentRemarks() {
    this._sharedFacade.resetStudentRemarks();
  }

  loadStudentLateArrivalsByDay(date: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.loadStudentsLateArrivalsByDay(school.id, date))
    ).subscribe(lateArrivals => this._store.dispatch(new StudentLateArrivalsLoaded(lateArrivals)));
  }

  loadStudentEarlyDeparturesByDay(date: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.loadStudentsEarlyDeparturesByDay(school.id, date))
    ).subscribe(earlyDepartures => this._store.dispatch(new StudentEarlyDeparturesLoaded(earlyDepartures)));
  }

  loadStudentExclusionsByDay(date: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.loadStudentsClassExclusionsByDay(school.id, date))
    ).subscribe(exclusions => this._store.dispatch(new StudentClassExclusionsLoaded(exclusions)));
  }

  loadSchoolDiaryByClass(classId: number, from: DateTime, to: DateTime) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._teacherPlanningService.loadSchoolDiaryByClass(school.id, classId, from, to))
    ).subscribe(schoolDiaryEntryEvents => this._store.dispatch(teacherPlanningEventsLoaded({planningEvents: schoolDiaryEntryEvents})));
  }

  loadSummerPermanences() {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.getSummerPermanences(school.id))
    ).subscribe(permanences => this._store.dispatch(new SummerPermanenceLoadedAction(permanences)));
  }

  saveSummerPermanence(permanence: SummerPermanence) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.saveSummerPermanence(school.id, permanence))
    ).subscribe(permanenceId => this._store.dispatch(new SummerPermanenceSavedAction({...permanence, id: permanenceId})));
  }

  deleteSummerPermanence(permanenceId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.removeSummerPermanence(school.id, permanenceId))
    ).subscribe(_ => this._store.dispatch(new SummerPermanenceDeletedAction(permanenceId)));
  }

  assignMeSummerPermanence(permanenceId: number) {
    combineLatest([this.school$, this.profile$]).pipe(
      take(1),
      switchMap(([school, profile]) =>
        this._schoolService.assignSummerPermanence(school.id, permanenceId).pipe(map(_ => profile)))
    ).subscribe(profile => this._store.dispatch(new SummerPermanenceAssignedAction({
      id: permanenceId,
      staffMemberId: profile.id,
      staffMemberFirstName: profile.firstName,
      staffMemberLastName: profile.lastName
    })));
  }

  removeAssignedSummerPermanence(permanenceId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school =>
        this._schoolService.removeAssignedSummerPermanence(school.id, permanenceId))
    ).subscribe(_ => this._store.dispatch(new SummerPermanenceUnassignedAction(permanenceId)));
  }

  loadAttendanceRegistries(schoolYear: number, year: string, month: string, classIds: number[]) {
    this.school$.pipe(
      take(1),
      switchMap(school => {
        const requests: Observable<AttendanceRegistryByClass>[] =  classIds.map(classId =>
          this._schoolService.getClassAttendanceRegistries(school.id, schoolYear, year, month, classId).pipe(
            map(attendanceRegistry => ({ classId: classId, registry: attendanceRegistry}))
          )
        );
        return forkJoin(requests);
      })
    ).subscribe(attendanceRegistries => 
      this._store.dispatch(new AttendanceRegistriesLoadedAction(attendanceRegistries))
    );
  }

  loadClassStaffMemberRelations() {
    this.profile$.pipe(
      take(1)
    ).subscribe(profile => {
      this._sharedFacade.loadClassStaffMemberRelations(profile.id);
    });
  }

  generateAttendanceRegistries(schoolYear: number, year: string, month: string) {
    this._store.dispatch(documentGenerationRequestStarted());
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.generateSchoolAttendanceRegistryReport(school.id, schoolYear, year, month))
    ).subscribe(
      (locationUrl) => this._sharedFacade.startPollingDocument(locationUrl, documentGenerationRequestStopped()),
      (err) => {
        this._store.dispatch(documentGenerationRequestStopped());
        throw err;
      });
  }

  resetAttendanceRegistries() {
    this._store.dispatch(new AttendanceRegistriesResetAction());
  }

  createSchoolDiaryByAttributionPdfGenerationRequest(attributionId: number) {
    this._store.dispatch(documentGenerationRequestStarted());
    this.school$.pipe(
      take(1),
      switchMap(school => this._teacherPlanningService.createSchoolDiaryByAttributionPdfGenerationRequest(school.id, attributionId))
    ).subscribe(
      {
        next: (locationUrl) => this._sharedFacade.startPollingDocument(locationUrl, documentGenerationRequestStopped()),
        error: (err) => {
          this._store.dispatch(documentGenerationRequestStopped());
          throw err;
        }
      });
  }

  loadSchoolSettings(): void {
    this._sharedFacade.loadSchoolSettings();
  }

  loadSharedDocuments(): void {
    this._sharedFacade.loadSharedDocuments();
  }

  uploadSharedDocument(file: File): void {
    this.school$.pipe(
      take(1),
      switchMap(school => this._sharedDocumentService.uploadSharedDocument(school.id, file))
    ).subscribe(sharedDocument => this._store.dispatch(SharedDocumentUploaded({sharedDocument})));
  }

  downloadSharedDocument(file: SharedDocument): void {
    this._sharedFacade.downloadSharedDocument(file);
  }

  deleteSharedDocument(fileId: string): void {
    this.school$.pipe(
      take(1),
      switchMap(school => this._sharedDocumentService.deleteSharedDocument(school.id, fileId))
    ).subscribe(_ => this._store.dispatch(SharedDocumentDeleted({sharedDocumentId: fileId})));
  }

  updatePermittedUserGroups(fileId: string, permittedUserGroups: string[]): void {
    this.school$.pipe(
      take(1),
      switchMap(school => this._sharedDocumentService.updateSharedDocument(school.id, fileId, permittedUserGroups))
    ).subscribe(sharedDocument => this._store.dispatch(SharedDocumentUploaded({sharedDocument})));
  }

  downloadLegalAbsenceDocument(studentId: number, justificationId: number, documentId: string): void {
    this._sharedFacade.downloadLegalAbsenceDocument(studentId, justificationId, documentId);
  }

  loadHorizonClassLinks() {
    this._sharedFacade.loadHorizonClassLinks();
  }
}
